import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import SessionContext from "../layout/SessionContext";
import { ClubOption, fetchAllClubs, fetchPlayerName, joinClub, PlayerName } from "./clubApi";

function JoinClubDialog({
  open,
  onClose,
  onJoined,
  devPort,
  playerId,
  joinedClubIds,
  betaMode,
}: {
  open: boolean;
  onClose: () => void;
  onJoined: (clubId: number, waitlist: boolean) => void;
  devPort: string;
  playerId: number;
  joinedClubIds: number[];
  betaMode: boolean;
}) {
  const { userName } = useContext(SessionContext);
  const [allClubs, setAllClubs] = useState<ClubOption[]>([]);
  const [club, setClub] = useState<ClubOption | null>(null);
  const [savedName, setSavedName] = useState<PlayerName | null | undefined>(undefined);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setClub(null);
      setError("");
      setSavedName(undefined);
      fetchAllClubs(devPort, betaMode)
        .then(setAllClubs)
        .catch(() => setError("We couldn't load the list of clubs."));
      fetchPlayerName(devPort, userName).then(setSavedName);
    }
  }, [open, devPort, betaMode, userName]);

  const options = allClubs.filter((option) => !joinedClubIds.includes(option.value));
  const needsName = savedName === null;
  const name = savedName || { firstName, lastName };
  const canSubmit =
    !!club &&
    savedName !== undefined &&
    !!name.firstName.trim() &&
    !!name.lastName.trim() &&
    !isSubmitting;

  function submit() {
    if (!club) {
      return;
    }
    setIsSubmitting(true);
    setError("");
    joinClub(devPort, playerId, club.value, name.firstName, name.lastName)
      .then((waitlist) => onJoined(club.value, waitlist))
      .catch(() => setError("We couldn't join that club. Please try again."))
      .finally(() => setIsSubmitting(false));
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" data-testid="join-club-dialog">
      <DialogTitle>Join a club</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            data-testid="join-club-name"
            options={options}
            value={club}
            onChange={(event, newValue) => setClub(newValue)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} label="Club Name" />}
          />
          {needsName && (
            <>
              <TextField
                inputProps={{ "data-testid": "join-first-name" }}
                label="First Name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
              <TextField
                inputProps={{ "data-testid": "join-last-name" }}
                label="Last Name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="join-club-submit"
        >
          Join
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default JoinClubDialog;
